import { gql } from '@apollo/client';
import {
  Box,
  chakra,
  FormControl,
  FormErrorMessage,
  FormLabel,
  GridItem,
  useToast,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select, { OptionProps, SingleValue } from 'react-select';
import { z } from 'zod';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigPlaylistSelect_ConfigFragment,
  ConfigPlaylistSelect_CustomerFragment,
  useConfigUpdatePlaylistContentSourceMutation,
} from './__generated__/ConfigDisplayPlaylist.graphql';

const playlistOption = selectOptionBaseSchema;

type PlaylistOption = z.TypeOf<typeof playlistOption>;

const schema = z.object({
  configId: z.string(),
  playlist: playlistOption.optional().nullable(),
});

type FormValues = z.TypeOf<typeof schema>;

export function ConfigPlaylistSelect({
  customer,
  config,
  configStatus,
}: {
  customer: ConfigPlaylistSelect_CustomerFragment;
  config: ConfigPlaylistSelect_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}) {
  const filteredOptions = useMemo<PlaylistOption[]>(() => {
    return customer.playlists
      .filter((p) => !config.playlistType || p.playlistType === config.playlistType)
      .map<PlaylistOption>((p) => ({
        label: p.title,
        value: p.id,
      }));
  }, [customer.playlists, config.playlistType]);
  const toast = useToast();

  const currentPlaylist = useMemo(
    () => filteredOptions.find((o) => o.value === config.playlist?.current?.id) || null,
    [config, filteredOptions],
  );

  const {
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      configId: config.id,
      playlist: currentPlaylist,
    },
    resolver: zodResolver(schema),
  });

  const [selectedPlaylist, setSelectedPlaylist] =
    useState<SingleValue<PlaylistOption>>(currentPlaylist);

  const { verifyUserPermissions } = useAuth();

  const isDisplaySettingsUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);

  const [updatePlaylist, { loading }] = useConfigUpdatePlaylistContentSourceMutation();

  useEffect(() => {
    setSelectedPlaylist(currentPlaylist);
  }, [currentPlaylist]);

  const handlePlaylistSelect = async (selected: PlaylistOption | null) => {
    try {
      await updatePlaylist({
        variables: {
          input: {
            configurationId: config.id,
            playlistId: selected ? selected.value : null,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t('errorUpdatingPlaylist'),
      });
    }
  };

  return (
    <GridItem
      colSpan={{
        base: 8,
        sm: 6,
        lg: 8,
        xl: 5,
      }}
    >
      <FormControl isInvalid={Boolean(errors.playlist)}>
        <FormLabel w={'100%'}>
          {t('selectPlaylist')}
          <FormLabelPendingIndicator isPending={loading} />
        </FormLabel>
        <Controller
          name="playlist"
          control={control}
          render={({ field }) => (
            <Select
              value={selectedPlaylist}
              options={filteredOptions}
              components={{
                ...components,
                Option: ManagePlaylistSelectOption,
              }}
              onChange={(selected) => {
                field.onChange(selected);
                handlePlaylistSelect(selected);
              }}
              isLoading={isSubmitting}
              placeholder={t('select')}
              isMulti={false}
              escapeClearsValue={false}
              isClearable={true}
              isSearchable={true}
              menuPlacement="auto"
              isDisabled={isDisplaySettingsUpdateDisabled}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="powerSchedule"
          render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
        />
      </FormControl>
    </GridItem>
  );
}

export function ManagePlaylistSelectOption({
  isSelected,
  innerProps,
  ...rest
}: OptionProps<PlaylistOption, false>) {
  const data = rest.data;

  return (
    <Box
      display="flex"
      alignItems="center"
      paddingX="3.5"
      paddingY="2"
      cursor="pointer"
      color={isSelected ? 'white' : 'blue.800'}
      background={isSelected ? 'blue.400' : 'white'}
      fontSize="md"
      _hover={{
        background: isSelected ? 'blue.400' : 'blue.50',
      }}
      {...innerProps}
    >
      <chakra.span display="inline-block" flex="1">
        {data.label}
      </chakra.span>
    </Box>
  );
}

ConfigPlaylistSelect.graphql = {
  fragments: {
    ConfigPlaylistSelect_customer: gql`
      fragment ConfigPlaylistSelect_customer on Customer {
        id
        playlists {
          id
          size
          title
          playlistType
        }
      }
    `,
    ConfigPlaylistSelect_config: gql`
      fragment ConfigPlaylistSelect_config on ConfigurationModel {
        id
        playlistType
        playlist {
          current {
            id
            title
            size
            __typename
          }
          __typename
        }
      }
    `,
  },
  mutations: {
    ConfigUpdatePlaylistContentSource: gql`
      mutation ConfigUpdatePlaylistContentSource(
        $input: ConfigurationUpdatePlaylistContentSourceInput!
      ) {
        configurationUpdatePlaylistContentSource(input: $input) {
          id
        }
      }
    `,
  },
};
